import { baseColors } from '@ops-matrix/design-system-ui';
import { TracingErrorBoundary, TracingProvider } from '@ops-matrix/design-system-ui/tracing';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { env } from './config/env';
import { TRPCProviderInternal } from './lib/trpc/provider';
import { ThemeProvider } from './theme/ThemeProvider';
import './index.css';

/**
 * Browser tracing configuration (Issue #469)
 * Connects frontend spans to backend OpenTelemetry infrastructure
 *
 * IMPORTANT: OTel is disabled by default in production to avoid CORS errors.
 * Set VITE_OTEL_COLLECTOR_URL environment variable to enable (no localhost fallback).
 */
const tracingConfig = {
  serviceName: 'auth-portal',
  serviceVersion: '1.0.0',
  collectorUrl: env.VITE_OTEL_COLLECTOR_URL ?? 'http://localhost:4318/v1/traces',
  // Only enable if collector URL is explicitly set (not localhost fallback) and not disabled
  // This prevents browser tracing errors in staging/production where no collector is available
  enabled: !!env.VITE_OTEL_COLLECTOR_URL && import.meta.env.VITE_OTEL_ENABLED !== 'false',
  sampleRate: env.VITE_OTEL_SAMPLE_RATE ?? (import.meta.env.DEV ? 1.0 : 0.1),
  propagateTraceHeaderOrigins: [env.VITE_API_URL],
};

/**
 * Error fallback component for the error boundary
 */
function ErrorFallback(error: Error) {
  return (
    <div
      style={{
        padding: '20px',
        fontFamily: 'monospace',
        backgroundColor: baseColors.error[50],
        minHeight: '100vh',
      }}
    >
      <h1 style={{ color: baseColors.error[700] }}>Something went wrong</h1>
      <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {error.message}
        {'\n\n'}
        {error.stack}
      </pre>
    </div>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5000,
      gcTime: 300000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <TracingProvider config={tracingConfig}>
      <TracingErrorBoundary fallback={ErrorFallback}>
        <QueryClientProvider client={queryClient}>
          <TRPCProviderInternal>
            <BrowserRouter>
              <ThemeProvider>
                <App />
              </ThemeProvider>
            </BrowserRouter>
          </TRPCProviderInternal>
        </QueryClientProvider>
      </TracingErrorBoundary>
    </TracingProvider>
  </React.StrictMode>,
);
